<template>
  <div class="autocomplete-wrapper">

    <label class="hint">
      Начните вводить название университета. Если его нет — можно ввести вручную.
    </label>


    <div
      class="input-wrapper"
      :class="{
        active: !!selectedUniversity,
        focused: isFocused
      }"
    >

      <input
        v-if="!selectedUniversity"
        v-model="query"
        type="text"
        class="search-input"
        placeholder="Университет"
        @input="onInput"
        @focus="isFocused = true"
        @blur="handleBlur"
      />


      <div
        v-else
        class="selected-display"
      >
        <div class="selected-university">

          <div class="selected-icon">
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 7.2L10 3.5L17 7.2L10 10.9L3 7.2Z"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linejoin="round"
              />

              <path
                d="M5.5 8.8V12.8C5.5 14.1 7.5 15.4 10 15.4C12.5 15.4 14.5 14.1 14.5 12.8V8.8"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </div>


          <span class="selected-name">
            {{ selectedUniversity.name }}
          </span>

        </div>


        <button
          class="clear-btn"
          type="button"
          aria-label="Очистить"
          @click="clearSelection"
        >
          <svg
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5.5 5.5L12.5 12.5M12.5 5.5L5.5 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>


      <button
        v-if="
          query &&
          !isLoading &&
          !results.length &&
          !selectedUniversity
        "
        class="add-btn"
        type="button"
        @mousedown.prevent
        @click="selectCustomUniversity"
      >
        Добавить
      </button>

    </div>


    <!-- =====================================================
         RESULTS
    ====================================================== -->

    <transition name="results-fade">

      <div
        v-if="query && !selectedUniversity"
        class="results"
      >

        <!-- LOADING -->

        <div
          v-if="isLoading"
          class="results-state"
        >
          <div class="loading-spinner"></div>

          <span>
            Ищем университеты...
          </span>
        </div>


        <!-- RESULTS -->

        <div
          v-else-if="results.length"
          class="results-list"
        >

          <button
            v-for="university in results"
            :key="university.id"
            type="button"
            class="university-item"
            @mousedown.prevent
            @click="selectUniversity(university)"
          >

            <div class="university-icon">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 7.2L10 3.5L17 7.2L10 10.9L3 7.2Z"
                  stroke="currentColor"
                  stroke-width="1.35"
                  stroke-linejoin="round"
                />

                <path
                  d="M5.5 8.8V12.8C5.5 14.1 7.5 15.4 10 15.4C12.5 15.4 14.5 14.1 14.5 12.8V8.8"
                  stroke="currentColor"
                  stroke-width="1.35"
                  stroke-linecap="round"
                />
              </svg>
            </div>


            <div class="info">
              <div class="name">
                {{ university.name }}
              </div>
            </div>


            <svg
              class="result-arrow"
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 5L11 9L7 13"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

          </button>

        </div>


        <!-- EMPTY -->

        <div
          v-else
          class="no-results"
        >
          <div class="no-results-title">
            Университет не найден
          </div>

          <div class="no-results-text">
            Можно добавить
            <strong>{{ query }}</strong>
            вручную
          </div>
        </div>

      </div>

    </transition>

  </div>
</template>


<script setup>

import {
  ref
} from "vue";

import debounce
  from "lodash/debounce";

import SummaryService
  from "../../api/SummaryService";


const emit =
  defineEmits([
    "update:modelValue"
  ]);


const query =
  ref("");

const results =
  ref([]);

const isLoading =
  ref(false);

const selectedUniversity =
  ref(null);

const isFocused =
  ref(false);


/* ============================================================
   SEARCH
============================================================ */

const onInput =
  debounce(
    async () => {

      const normalizedQuery =
        query.value.trim();


      if (!normalizedQuery) {

        results.value =
          [];

        isLoading.value =
          false;

        return;

      }


      isLoading.value =
        true;


      try {

        const response =
          await SummaryService
            .searchUniversities(
              normalizedQuery
            );


        results.value =
          Array.isArray(response)
            ? response
            : [];

      } catch (error) {

        console.error(
          "❌ Ошибка поиска университетов:",
          error
        );


        results.value =
          [];

      } finally {

        isLoading.value =
          false;

      }

    },
    400
  );


/* ============================================================
   SELECT
============================================================ */

const selectUniversity =
  (university) => {

    selectedUniversity.value =
      university;

    query.value =
      university?.name || "";

    results.value =
      [];


    emit(
      "update:modelValue",
      university
    );

  };


/* ============================================================
   CUSTOM UNIVERSITY
============================================================ */

const selectCustomUniversity =
  () => {

    const name =
      query.value.trim();


    if (!name) {
      return;
    }


    const customUniversity = {

      id:
        `custom-${Date.now()}`,

      name

    };


    selectUniversity(
      customUniversity
    );

  };


/* ============================================================
   CLEAR
============================================================ */

const clearSelection =
  () => {

    selectedUniversity.value =
      null;

    query.value =
      "";

    results.value =
      [];


    emit(
      "update:modelValue",
      null
    );

  };


/* ============================================================
   BLUR
============================================================ */

const handleBlur =
  () => {

    isFocused.value =
      false;

  };

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.autocomplete-wrapper {
  width: 100%;

  max-width: none;

  position: relative;
}


/* ============================================================
   HINT
============================================================ */

.hint {
  display: block;

  margin:
    0
    0
    9px;

  color:
    #9ca1a9;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.45;
}


/* ============================================================
   INPUT CONTAINER
============================================================ */

.input-wrapper {
  min-height:
    50px;

  width:
    100%;

  position:
    relative;

  display:
    flex;

  align-items:
    center;

  box-sizing:
    border-box;

  padding:
    0
    14px;

  background:
    #f4f5f7;

  border:
    1px solid
    rgba(35, 45, 60, .035);

  border-radius:
    14px;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    box-shadow .18s ease;
}


.input-wrapper:hover {
  background:
    #f0f2f5;
}


.input-wrapper.focused {
  background:
    #ffffff;

  border-color:
    rgba(52, 127, 231, .48);

  box-shadow:
    0
    0
    0
    4px
    rgba(52, 127, 231, .08);
}


.input-wrapper.active {
  background:
    rgba(52, 127, 231, .045);

  border-color:
    rgba(52, 127, 231, .20);
}


/* ============================================================
   SEARCH INPUT
============================================================ */

.search-input {
  width:
    100%;

  min-width:
    0;

  height:
    48px;

  padding:
    0;

  color:
    #2f333a;

  background:
    transparent;

  border:
    none;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    13px;

  line-height:
    1;
}


.search-input::placeholder {
  color:
    #a2a7af;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   ADD BUTTON
============================================================ */

.add-btn {
  height:
    30px;

  flex:
    0
    0
    auto;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  margin-left:
    10px;

  padding:
    0
    12px;

  color:
    #ffffff;

  background:
    #347fe7;

  border:
    none;

  border-radius:
    9px;

  box-shadow:
    0
    5px
    12px
    rgba(52, 127, 231, .15);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    transform .18s ease,
    box-shadow .18s ease;
}


.add-btn:hover {
  background:
    #2c74d7;

  transform:
    translateY(-1px);

  box-shadow:
    0
    7px
    15px
    rgba(52, 127, 231, .20);
}


/* ============================================================
   SELECTED
============================================================ */

.selected-display {
  width:
    100%;

  min-width:
    0;

  min-height:
    48px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    12px;
}


.selected-university {
  min-width:
    0;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;
}


.selected-icon {
  width:
    30px;

  height:
    30px;

  flex:
    0
    0
    30px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #5f87bd;

  background:
    rgba(52, 127, 231, .08);

  border:
    1px solid
    rgba(52, 127, 231, .08);

  border-radius:
    9px;
}


.selected-icon svg {
  width:
    16px;

  height:
    16px;
}


.selected-name {
  min-width:
    0;

  overflow:
    hidden;

  color:
    #424851;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.35;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


/* ============================================================
   CLEAR
============================================================ */

.clear-btn {
  width:
    30px;

  height:
    30px;

  flex:
    0
    0
    30px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    0;

  color:
    #777e88;

  background:
    rgba(30, 40, 55, .045);

  border:
    1px solid
    rgba(30, 40, 55, .045);

  border-radius:
    9px;

  cursor:
    pointer;

  transition:
    color .18s ease,
    background-color .18s ease,
    transform .18s ease;
}


.clear-btn svg {
  width:
    15px;

  height:
    15px;
}


.clear-btn:hover {
  color:
    #49515c;

  background:
    rgba(30, 40, 55, .075);

  transform:
    scale(1.04);
}


/* ============================================================
   RESULTS
============================================================ */

.results {
  width:
    100%;

  max-height:
    260px;

  box-sizing:
    border-box;

  margin-top:
    8px;

  overflow:
    hidden;

  background:
    rgba(255, 255, 255, .98);

  border:
    1px solid
    rgba(35, 45, 60, .065);

  border-radius:
    14px;

  box-shadow:
    0
    14px
    34px
    rgba(28, 40, 60, .10);
}


/* ============================================================
   RESULTS LIST
============================================================ */

.results-list {
  max-height:
    260px;

  overflow-y:
    auto;

  scrollbar-width:
    thin;

  scrollbar-color:
    rgba(120, 125, 135, .18)
    transparent;
}


.results-list::-webkit-scrollbar {
  width:
    5px;
}


.results-list::-webkit-scrollbar-thumb {
  background:
    rgba(120, 125, 135, .18);

  border-radius:
    999px;
}


/* ============================================================
   RESULT
============================================================ */

.university-item {
  width:
    100%;

  min-height:
    54px;

  display:
    flex;

  align-items:
    center;

  gap:
    11px;

  box-sizing:
    border-box;

  padding:
    9px
    12px;

  color:
    inherit;

  background:
    transparent;

  border:
    none;

  border-bottom:
    1px solid
    rgba(35, 45, 60, .045);

  text-align:
    left;

  cursor:
    pointer;

  transition:
    background-color .16s ease;
}


.university-item:last-child {
  border-bottom:
    none;
}


.university-item:hover {
  background:
    #f5f7fa;
}


.university-icon {
  width:
    32px;

  height:
    32px;

  flex:
    0
    0
    32px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #788ca8;

  background:
    #f0f3f7;

  border-radius:
    9px;
}


.university-icon svg {
  width:
    16px;

  height:
    16px;
}


.info {
  min-width:
    0;

  flex:
    1;
}


.info .name {
  overflow:
    hidden;

  color:
    #41464e;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.35;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.result-arrow {
  width:
    16px;

  height:
    16px;

  flex:
    0
    0
    16px;

  color:
    #a1a6ad;

  transition:
    transform .16s ease,
    color .16s ease;
}


.university-item:hover
.result-arrow {
  color:
    #6e8ebc;

  transform:
    translateX(2px);
}


/* ============================================================
   STATES
============================================================ */

.results-state,
.no-results {
  min-height:
    76px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  padding:
    16px;

  color:
    #9298a1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;
}


.results-state {
  gap:
    9px;
}


.loading-spinner {
  width:
    17px;

  height:
    17px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(52, 127, 231, .14);

  border-top-color:
    #347fe7;

  border-radius:
    50%;

  animation:
    autocompleteSpin
    .65s
    linear
    infinite;
}


.no-results {
  flex-direction:
    column;

  gap:
    4px;

  text-align:
    center;
}


.no-results-title {
  color:
    #626973;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;
}


.no-results-text {
  color:
    #989ea7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  line-height:
    1.4;
}


.no-results-text strong {
  color:
    #6687b3;

  font-family:
    "Eastman-Medium",
    sans-serif;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.hint {
  color:
    #747981;
}


html[data-theme="dark"]
.input-wrapper {
  background:
    #292b30;

  border-color:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.input-wrapper:hover {
  background:
    #2d3035;
}


html[data-theme="dark"]
.input-wrapper.focused {
  background:
    #27292e;

  border-color:
    rgba(91, 158, 255, .50);

  box-shadow:
    0
    0
    0
    4px
    rgba(91, 158, 255, .075);
}


html[data-theme="dark"]
.input-wrapper.active {
  background:
    rgba(91, 158, 255, .055);

  border-color:
    rgba(91, 158, 255, .16);
}


html[data-theme="dark"]
.search-input {
  color:
    #e8e9eb;
}


html[data-theme="dark"]
.search-input::placeholder {
  color:
    #747981;
}


html[data-theme="dark"]
.selected-icon {
  color:
    #91b6ee;

  background:
    rgba(91, 158, 255, .075);

  border-color:
    rgba(91, 158, 255, .08);
}


html[data-theme="dark"]
.selected-name {
  color:
    #e3e5e8;
}


html[data-theme="dark"]
.clear-btn {
  color:
    #8e949d;

  background:
    rgba(255, 255, 255, .045);

  border-color:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.clear-btn:hover {
  color:
    #c5c9ce;

  background:
    rgba(255, 255, 255, .075);
}


html[data-theme="dark"]
.results {
  background:
    #22252b;

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    16px
    38px
    rgba(0, 0, 0, .28);
}


html[data-theme="dark"]
.university-item {
  border-bottom-color:
    rgba(255, 255, 255, .05);
}


html[data-theme="dark"]
.university-item:hover {
  background:
    rgba(255, 255, 255, .04);
}


html[data-theme="dark"]
.university-icon {
  color:
    #8599b4;

  background:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.info .name {
  color:
    #e1e3e6;
}


html[data-theme="dark"]
.result-arrow {
  color:
    #666d77;
}


html[data-theme="dark"]
.university-item:hover
.result-arrow {
  color:
    #91b6ee;
}


html[data-theme="dark"]
.results-state {
  color:
    #838a94;
}


html[data-theme="dark"]
.no-results-title {
  color:
    #b3b8c0;
}


html[data-theme="dark"]
.no-results-text {
  color:
    #777e88;
}


html[data-theme="dark"]
.no-results-text strong {
  color:
    #91b6ee;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 640px) {

  .hint {
    font-size:
      9px;
  }


  .input-wrapper {
    min-height:
      48px;

    padding:
      0
      12px;

    border-radius:
      13px;
  }


  .search-input {
    height:
      46px;

    font-size:
      12px;
  }


  .selected-display {
    min-height:
      46px;
  }


  .selected-name {
    font-size:
      10px;
  }


  .results {
    max-height:
      220px;
  }


  .results-list {
    max-height:
      220px;
  }

}


/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes autocompleteSpin {

  to {
    transform:
      rotate(360deg);
  }

}


.results-fade-enter-active,
.results-fade-leave-active {
  transition:
    opacity .16s ease,
    transform .16s ease;
}


.results-fade-enter-from,
.results-fade-leave-to {
  opacity:
    0;

  transform:
    translateY(-4px);
}

</style>